//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { CaptionSecondaryText, CaptionText } from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { Mixins } from '@theme/mixins';

const VISIBLE_IDENTIFIER_COUNT = 2;

interface IdentifierRowProps {
  identifiers?: string[];
  label: 'EAN' | 'UPC';
}

export const IdentifierRow = ({ identifiers, label }: IdentifierRowProps) => {
  if (!identifiers?.length) {
    return null;
  }

  const visibleIdentifiers = identifiers
    .slice(0, VISIBLE_IDENTIFIER_COUNT)
    .join(', ');
  const additionalIdentifierCount =
    identifiers.length - VISIBLE_IDENTIFIER_COUNT;
  const additionalIdentifiersLabel = `+${additionalIdentifierCount} more`;
  const trackingId =
    label === 'UPC'
      ? SAMPLING_CAMPAIGNS_TRACKING_ID.CATALOG_SEARCH_MODAL_UPC_POPOVER
      : SAMPLING_CAMPAIGNS_TRACKING_ID.CATALOG_SEARCH_MODAL_EAN_POPOVER;

  return (
    <IdentifierRowContainer>
      <IdentifierSummary>
        <CaptionSecondaryText ellipsize={1} whiteSpace='nowrap'>
          {label}: {visibleIdentifiers}
        </CaptionSecondaryText>
      </IdentifierSummary>
      {additionalIdentifierCount > 0 && (
        <Popover
          appendTarget='body'
          content={
            <IdentifiersPopoverContent>
              {identifiers.map((identifier) => (
                <CaptionText key={identifier}>{identifier}</CaptionText>
              ))}
            </IdentifiersPopoverContent>
          }
          placement='bottom-start'
          shouldOpenOnHover={false}
          target={
            <AdditionalIdentifiersButton
              textMixinOverride={Mixins.Text.CaptionSecondary}
              trackingId={trackingId}
            >
              {additionalIdentifiersLabel}
            </AdditionalIdentifiersButton>
          }
        />
      )}
    </IdentifierRowContainer>
  );
};

const IdentifierRowContainer = styled.div(
  () => `
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;

    > :last-child {
      flex: 0 0 auto;
    }
  `,
);

const IdentifierSummary = styled.div`
  flex: 0 1 auto;
  min-width: 0;

  > div {
    min-width: 0;
    width: 100%;
  }
`;

const AdditionalIdentifiersButton = styled(PrimaryLinkButton)(
  ({ theme }) => `
    margin-left: ${theme.echo.spacing.xxs};
  `,
);

const IdentifiersPopoverContent = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.echo.spacing.xxs};
    padding: ${theme.echo.spacing.xs};
  `,
);
